import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import HelpIcon from './help/HelpIcon';
import { listOrganizationUsers, updateRestaurantSettings } from '../services/api';
import { roleUsesPin } from '../utils/comandaUsers';
import {
  DEFAULT_COMANDA_SECURITY,
  buildComandaSecurityPayload,
  countUsersWithoutPin,
  isComandaSecurityDirty,
  toComandaSecurityForm,
  validateComandaSecurity,
} from '../utils/comandaSecurity';
import { storage } from '../utils/storage';

/**
 * "Seguridad de la comanda" (RES-114): la identificación por mozo y el auto-bloqueo viven en el
 * servidor, por local, para que todas las tablets los respeten (antes el bloqueo era una
 * preferencia local del navegador). Se guarda en `settings.comandaSecurity` con
 * `PATCH /restaurants/{id}/settings`.
 *
 * @param {object} props
 * @param {string} props.restaurantId - Local seleccionado
 * @param {boolean} props.loading - El restaurante todavía se está cargando
 * @param {object|null} props.savedConfig - `settings.comandaSecurity` guardado (o null)
 * @param {boolean} props.featureAvailable - El plan incluye `staffIdentification`
 * @param {Function} props.onUpsell - Abre el upsell del plan (switch bloqueado o 403 del servidor)
 * @param {Function} [props.onDirtyChange] - Avisa al contenedor si hay cambios sin guardar
 * @param {Function} [props.onSaved] - Recibe lo guardado
 */
function ComandaSecuritySection({
  restaurantId,
  loading,
  savedConfig,
  featureAvailable,
  onUpsell,
  onDirtyChange,
  onSaved,
}) {
  const { t } = useTranslation();

  const [saved, setSaved] = useState(() => toComandaSecurityForm(savedConfig));
  const [form, setForm] = useState(() => toComandaSecurityForm(savedConfig));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [users, setUsers] = useState(null);

  // Cuando el restaurante termina de cargar (o cambia) se parte de lo guardado en el servidor
  useEffect(() => {
    const next = toComandaSecurityForm(savedConfig);
    setSaved(next);
    setForm(next);
  }, [savedConfig]);

  const dirty = isComandaSecurityDirty(form, saved);

  useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  // Con la identificación activa, cuántos usuarios del local todavía no tienen PIN
  useEffect(() => {
    const organizationId = storage.get('selectedOrganizationId');
    if (!form.staffIdentification || !organizationId || !restaurantId) return undefined;

    let cancelled = false;
    listOrganizationUsers(organizationId)
      .then((response) => {
        if (!cancelled) setUsers(response?.users || []);
      })
      .catch((err) => {
        console.error('Error loading users for PIN count:', err);
        if (!cancelled) setUsers(null);
      });
    return () => {
      cancelled = true;
    };
  }, [form.staffIdentification, restaurantId]);

  const withoutPin = useMemo(
    () => (users ? countUsersWithoutPin(users, restaurantId, roleUsesPin) : null),
    [users, restaurantId]
  );

  const update = (patch) => {
    setSuccess('');
    setError('');
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const handleToggleIdentification = (checked) => {
    if (checked && !featureAvailable) {
      // El plan no lo incluye: se muestra el upsell y el switch queda como estaba
      onUpsell();
      return;
    }
    update({
      staffIdentification: checked,
      // exigir operador no tiene sentido sin identificación (el servidor lo rechaza)
      requireOperator: checked ? form.requireOperator : false,
    });
  };

  const handleSave = async () => {
    const validationError = validateComandaSecurity(form);
    if (validationError) {
      setSuccess('');
      setError(t(`settings.comandaSecurity.errors.${validationError}`));
      return;
    }

    try {
      setSaving(true);
      setError('');
      const payload = buildComandaSecurityPayload(form);
      await updateRestaurantSettings(restaurantId, { comandaSecurity: payload });

      const next = toComandaSecurityForm(payload);
      setSaved(next);
      setForm(next);
      setSuccess(t('settings.comandaSecurity.saveSuccess'));
      setTimeout(() => setSuccess(''), 3000);
      if (onSaved) onSaved(payload);
    } catch (err) {
      console.error('Error saving comanda security:', err);
      if (err.response?.status === 403) {
        // Feature gate del servidor: el plan no incluye identificación por mozo
        onUpsell();
      } else if (err.response?.status === 400 && err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError(t('settings.comandaSecurity.saveError'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card settings-section" data-testid="comanda-security-section">
      <div className="section-header">
        <Icon name="badge" />
        <h2>{t('settings.comandaSecurity.title')}</h2>
        <HelpIcon topicKey="comandaSecurity.settings" />
        {dirty && <span className="dirty-badge">{t('common.unsavedChangesTitle')}</span>}
      </div>

      <div className="section-content">
        {success && (
          <div className="alert alert-success">
            <Icon name="check_circle" />
            <span>{success}</span>
          </div>
        )}
        {error && (
          <div className="alert alert-error" data-testid="comanda-security-error">
            <Icon name="error" />
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="loading-state">
            <Icon name="hourglass_empty" />
            <span>{t('common.loading')}</span>
          </div>
        ) : (
          <>
            <p className="setting-description comanda-security-intro">
              {t('settings.comandaSecurity.subtitle')}
            </p>

            {/* Identificación por mozo */}
            <div className="setting-item">
              <div className="setting-info">
                <h3>{t('settings.comandaSecurity.staffIdentification')}</h3>
                <p className="setting-description">
                  {t('settings.comandaSecurity.staffIdentificationDescription')}
                </p>
                {!featureAvailable && (
                  <p className="setting-description" data-testid="comanda-security-plan-note">
                    {t('settings.comandaSecurity.planRequired')}
                  </p>
                )}
              </div>
              <div className="setting-action">
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={form.staffIdentification}
                    aria-label={t('settings.comandaSecurity.staffIdentification')}
                    onChange={(e) => handleToggleIdentification(e.target.checked)}
                  />
                  <span className="slider round"></span>
                </label>
              </div>
            </div>

            {form.staffIdentification && withoutPin !== null && withoutPin > 0 && (
              <div className="alert alert-warning" data-testid="comanda-security-without-pin">
                <Icon name="warning" />
                <span>
                  {t('settings.comandaSecurity.usersWithoutPin', { count: withoutPin })}{' '}
                  <Link to="/users">{t('settings.comandaSecurity.goToUsers')}</Link>
                </span>
              </div>
            )}

            {/* Auto-bloqueo */}
            <div className="setting-item">
              <div className="setting-info">
                <h3>{t('settings.comandaSecurity.autoLockMinutes')}</h3>
                <p className="setting-description" data-testid="comanda-security-autolock-text">
                  {t('settings.comandaSecurity.autoLockDescription', {
                    minutes: form.autoLockMinutes,
                  })}
                </p>
              </div>
              <div className="setting-action">
                <input
                  type="number"
                  className="comanda-security-number"
                  min={1}
                  max={60}
                  step={1}
                  value={form.autoLockMinutes}
                  aria-label={t('settings.comandaSecurity.autoLockMinutes')}
                  onChange={(e) => update({ autoLockMinutes: e.target.value })}
                />
                <span className="comanda-security-unit">
                  {t('settings.comandaSecurity.minutes')}
                </span>
              </div>
            </div>

            {form.staffIdentification && (
              <>
                {/* Duración del turno (vigencia del token de operador) */}
                <div className="setting-item">
                  <div className="setting-info">
                    <h3>{t('settings.comandaSecurity.operatorTokenHours')}</h3>
                    <p className="setting-description">
                      {t('settings.comandaSecurity.operatorTokenHoursDescription', {
                        hours: form.operatorTokenHours,
                      })}
                    </p>
                  </div>
                  <div className="setting-action">
                    <input
                      type="number"
                      className="comanda-security-number"
                      min={1}
                      max={24}
                      step={1}
                      value={form.operatorTokenHours}
                      aria-label={t('settings.comandaSecurity.operatorTokenHours')}
                      onChange={(e) => update({ operatorTokenHours: e.target.value })}
                    />
                    <span className="comanda-security-unit">
                      {t('settings.comandaSecurity.hours')}
                    </span>
                  </div>
                </div>

                {/* Exigir operador */}
                <div className="setting-item">
                  <div className="setting-info">
                    <h3>{t('settings.comandaSecurity.requireOperator')}</h3>
                    <p className="setting-description">
                      {t('settings.comandaSecurity.requireOperatorDescription')}
                    </p>
                  </div>
                  <div className="setting-action">
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={form.requireOperator}
                        aria-label={t('settings.comandaSecurity.requireOperator')}
                        onChange={(e) => update({ requireOperator: e.target.checked })}
                      />
                      <span className="slider round"></span>
                    </label>
                  </div>
                </div>
              </>
            )}

            <div className="setting-item billing-actions">
              <div className="setting-info" />
              <div className="setting-action">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={saving || !dirty}
                >
                  {saving ? (
                    <>
                      <Icon name="hourglass_empty" />
                      {t('common.loading')}
                    </>
                  ) : (
                    <>
                      <Icon name="save" />
                      {t('common.save')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export { DEFAULT_COMANDA_SECURITY };
export default ComandaSecuritySection;
